import type { MembershipSummary, SoftwareUpdatePage } from '@livechat/shared';
import { useEffect, useState } from 'react';

import { listSoftwareUpdates } from '../api.js';
import { useDateFormat } from '../date-format.js';

const PAGE_SIZE = 25;

function emptyPage(): SoftwareUpdatePage {
	return { items: [], page: 1, pageSize: PAGE_SIZE, total: 0 };
}

export function TenantUpdates({ membership }: { membership: MembershipSummary }) {
	const { formatDateTime } = useDateFormat();
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<SoftwareUpdatePage>(emptyPage);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();

	useEffect(() => setPage(1), [membership.tenantId]);

	useEffect(() => {
		setLoading(true);
		setError(undefined);
		void listSoftwareUpdates(membership.tenantId, { page, pageSize: PAGE_SIZE })
			.then(setResult)
			.catch((caught: unknown) =>
				setError(
					caught instanceof Error ? caught.message : 'Could not load software updates',
				),
			)
			.finally(() => setLoading(false));
	}, [membership.tenantId, page]);

	const pageCount = Math.max(1, Math.ceil(result.total / result.pageSize));
	const first = result.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = Math.min(result.total, result.page * result.pageSize);

	return (
		<div className="operations-page updates-page">
			<div className="subsection-heading">
				<div>
					<h3>Updates</h3>
					<p>Software updates installed on this live-chat platform.</p>
				</div>
			</div>
			{error && <div className="alert error">{error}</div>}
			<div className="table-card paginated-table">
				<div className="paginated-table-scroll" aria-busy={loading}>
					<div className="table-head updates-row standard-table-head">
						<span>Date/time</span>
						<span>Version</span>
						<span>Description</span>
					</div>
					{loading && <div className="operations-empty">Loading updates…</div>}
					{!loading &&
						result.items.map((update) => (
							<div className="table-row updates-row" key={update.version}>
								<time dateTime={update.deployedAt}>
									{formatDateTime(update.deployedAt)}
								</time>
								<span>{update.version}</span>
								<div className="software-update-description">
									<strong>{update.description}</strong>
									{(update.items.length > 1 ||
										update.items[0] !== update.description) && (
										<ul>
											{update.items.map((item, index) => (
												<li key={`${update.version}-${index}`}>{item}</li>
											))}
										</ul>
									)}
								</div>
							</div>
						))}
					{!loading && !result.items.length && (
						<div className="operations-empty">
							No software updates have been recorded yet.
						</div>
					)}
				</div>
				<footer className="data-pagination">
					<span>
						Showing {first}–{last} of {result.total}
					</span>
					<div>
						<button
							aria-label="Previous page"
							disabled={loading || page <= 1}
							onClick={() => setPage((current) => Math.max(1, current - 1))}
							type="button"
						>
							‹
						</button>
						<span>
							Page {result.page} of {pageCount}
						</span>
						<button
							aria-label="Next page"
							disabled={loading || page >= pageCount}
							onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
							type="button"
						>
							›
						</button>
					</div>
				</footer>
			</div>
		</div>
	);
}
